import React from 'react';
import { Briefcase, Building2, ChevronDown, Shield, User } from 'lucide-react';
import { Badge } from '@/ui/ui/badge';
import { Button } from '@/ui/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/ui/dropdown-menu';
import {
  type FacilityContextSource,
  shouldShowFacilitySelector,
} from '@/modules/account-context/core/accountContext';
import {
  EXPERIENCE_MODE_DESCRIPTIONS,
  EXPERIENCE_MODE_LABELS,
  type ExperienceMode,
} from '@/hooks/useExperienceMode';
import { cn } from '@/lib/utils';

interface ExperienceModePanelProps {
  facilityOptions: FacilityContextSource[];
  selectedFacilityId: string;
  onFacilityChange: (facilityId: string) => void;
  availableModes: ExperienceMode[];
  selectedMode: ExperienceMode;
  onModeChange: (mode: ExperienceMode) => void;
}

const MODE_ICONS: Record<ExperienceMode, React.ComponentType<{ className?: string }>> = {
  super_admin: Shield,
  player: User,
  business_admin: Briefcase,
  staff: Building2,
};

const MODE_BADGE_CLASSES: Record<ExperienceMode, string> = {
  super_admin: 'border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-200',
  player: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-200',
  business_admin: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-200',
  staff: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
};

const MODE_MENU_CLASSES: Record<ExperienceMode, string> = {
  super_admin: 'text-violet-700 dark:text-violet-200',
  player: 'text-sky-700 dark:text-sky-200',
  business_admin: 'text-amber-700 dark:text-amber-200',
  staff: 'text-emerald-700 dark:text-emerald-200',
};

export const ExperienceModePanel: React.FC<ExperienceModePanelProps> = ({
  facilityOptions,
  selectedFacilityId,
  onFacilityChange,
  availableModes,
  selectedMode,
  onModeChange,
}) => {
  // Normalize optional props so a transient shell render cannot crash the entire layout.
  const safeFacilityOptions = facilityOptions ?? [];
  const safeAvailableModes = availableModes ?? [];
  const activeModeLabel = EXPERIENCE_MODE_LABELS[selectedMode];
  const ActiveModeIcon = MODE_ICONS[selectedMode];

  return (
    <div className="space-y-1.5">
      <p className="px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-sidebar-foreground/55">
        Role
      </p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              'h-auto min-h-0 w-full min-w-0 justify-between gap-3 rounded-full border px-3 py-2 text-left shadow-none hover:opacity-95',
              MODE_BADGE_CLASSES[selectedMode],
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <ActiveModeIcon className="h-4 w-4 shrink-0" />
              <span className="truncate text-sm font-semibold">{activeModeLabel}</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={8} className="w-64">
          <DropdownMenuLabel>Role</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {safeAvailableModes.map((mode) => {
            const Icon = MODE_ICONS[mode];
            const active = selectedMode === mode;

            return (
              <DropdownMenuItem
                key={mode}
                onClick={() => onModeChange(mode)}
                className={cn('flex items-center gap-2 px-2 py-2', active && 'bg-accent text-accent-foreground')}
              >
                <Badge variant="outline" className={cn('gap-1.5', MODE_MENU_CLASSES[mode])}>
                  <Icon className="h-3.5 w-3.5" />
                  {EXPERIENCE_MODE_LABELS[mode]}
                </Badge>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedMode !== 'player' ? (
        <div className="space-y-1.5 px-1">
          <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-sidebar-foreground/55">Facility</div>
          {shouldShowFacilitySelector(safeFacilityOptions.length) ? (
            <Select value={selectedFacilityId} onValueChange={onFacilityChange}>
              <SelectTrigger className="h-8 w-full rounded-full border-sidebar-border/70 bg-background/80 text-xs">
                <SelectValue placeholder="Select facility" />
              </SelectTrigger>
              <SelectContent>
                {safeFacilityOptions.map((facility) => (
                  <SelectItem key={facility.facilityId} value={facility.facilityId}>
                    {facility.facilityName ?? 'Facility'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

